import React, { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  AlertModalInterface,
  IsModalOpenProps,
  MeasuresProps,
  UserDataProps,
  defaultStateAlertModal,
} from './QuantitativeContext.types';
import { Quantitatives } from '../services/protocols';
import { AlertModal } from '../components/AlertModal/AlertModal.component';

interface QuantitativesContextInterface {
  isModalOpen: IsModalOpenProps;
  setIsModalOpen: React.Dispatch<React.SetStateAction<IsModalOpenProps>>;
  resultsIsOpen: boolean;
  setResultsIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  measures: MeasuresProps;
  setMeasures: React.Dispatch<React.SetStateAction<MeasuresProps>>;
  alertModalIsOpen: AlertModalInterface;
  setAlertModalIsOpen: React.Dispatch<React.SetStateAction<AlertModalInterface>>;
  quantitativesData: Quantitatives | null;
  setQuantitativesData: React.Dispatch<React.SetStateAction<Quantitatives | null>>;
  userData: UserDataProps | null;
  setUserData: React.Dispatch<React.SetStateAction<UserDataProps | null>>;
}

export const QuantitativesContext = createContext({} as QuantitativesContextInterface);

interface QuantitativesContextProviderProps {
  children: ReactNode;
}

export function QuantitativesContextProvider({ children }: QuantitativesContextProviderProps) {
  const [isModalOpen, setIsModalOpen] = useState<IsModalOpenProps>({
    open: null,
    addNewQuantitativo: false,
    isExported: false,
  });
  const [resultsIsOpen, setResultsIsOpen] = useState<boolean>(false);
  const [measures, setMeasures] = useState<MeasuresProps>({
    largura: undefined,
    comprimento: undefined,
    altura: 0,
    distanciaPort: 0,
  });
  const [alertModalIsOpen, setAlertModalIsOpen] =
    useState<AlertModalInterface>(defaultStateAlertModal);
  const [quantitativesData, setQuantitativesData] = useState<Quantitatives | null>(null);
  const [userData, setUserData] = useState<UserDataProps | null>(null);

  const value = useMemo(
    () => ({
      isModalOpen,
      setIsModalOpen,
      resultsIsOpen,
      setResultsIsOpen,
      measures,
      setMeasures,
      alertModalIsOpen,
      setAlertModalIsOpen,
      quantitativesData,
      setQuantitativesData,
      userData,
      setUserData,
    }),
    [
      isModalOpen,
      resultsIsOpen,
      measures,
      alertModalIsOpen,
      quantitativesData,
      setMeasures,
      userData,
      setIsModalOpen,
    ],
  );

  return (
    <QuantitativesContext.Provider value={value}>
      {children}
      {alertModalIsOpen?.open && (
        <AlertModal
          title={alertModalIsOpen.title}
          textSubmit={alertModalIsOpen.textSubmit}
          textCancel={alertModalIsOpen.textCancel}
          description={alertModalIsOpen.description}
          icon={alertModalIsOpen.icon}
          loader={alertModalIsOpen.loader}
          useAlertModal={() => ({
            isOpen: true,
            onCancel: alertModalIsOpen?.onCancel,
            onClose: () => setAlertModalIsOpen(defaultStateAlertModal),
            onSubmit: alertModalIsOpen.onSubmit,
            submitDangerous: alertModalIsOpen.submitDangerous,
          })}
        />
      )}
    </QuantitativesContext.Provider>
  );
}

export const useQuantitatives = (): QuantitativesContextInterface => {
  const context = useContext(QuantitativesContext);

  return context;
};
